/* =============================================================================
   VIEWS / ADMIN / ORDERS — Cockpit-Bestellungs-Tab (Orchestrator)
   =============================================================================
   Operative Pipeline für Takeaway-Bestellungen (4-Spalten-Kanban + Erledigt/
   Carry-Over/Schwund). S201 REF-7e: das Board ist in Module zerlegt (Null
   Verhaltensänderung) — orders.js ist jetzt der schlanke Orchestrator:
     · renderOrders (Board-Aufbau) + Live-View-Registry
     · Order-Detail-Modal (openOrderDetail + Bon-Vorschau/-Druck-Helfer)
     · Re-Exports (Board-Handler + Correction/Receipt-Action) für API/Test-Stabilität
     · window-Bindings für die orchestrator-eigenen Inline-Handler
   Extrahierte Module:
     · orders-data.js     — Konstanten + pure Selektoren/Sortierung (Vitest-Kern)
     · orders-actions.js  — Status-/Zahlungs-Handler + _probesaftAck-Set
     · orders-schwund.js  — „Schwund heute"-Block
     · orders-cards.js    — Kanban-Karten-Render
============================================================================ */

import {
  htmlEscape, fmtDate, fmtDateShort, fmtEUR, showToast,
  formatCustomizations, dateToISO,
  // S66 Refactor: Time/Display-Helper aus helpers.js (Single-Source)
  shortTime, formatTs, formatMinutes
} from '../../utils/helpers.js';
import { printOrderReceipt, renderOrderReceipt, attachReceiptTseQr, deliveryFeeReceiptInfo } from './receipt.js';
import { buildOrderTrackingLink } from '../../core/email-templates.js';
import { countOrdersByBucket } from '../../core/order-statuses.js';
import { orderChannels } from '../../core/order-items-cache.js';
import { registerLiveView } from '../../core/live-view-registry.js';
// S245 Cut C — Hinweisband, geteilt mit dem Belegungsplan.
import { mountSignalBand, renderSignalBandInto } from './table-signals.js';
// S248 — Teilzahlungen auf Karte und im Detail (eine Quelle, ein Ladeweg).
import { refreshReceiptSummaries, partialPayFor } from './order-receipt-summary.js';
// S253 — Küchen-Hinweise der Gäste am Tisch (phase_185).
import {
  guestNotes, notesForOccupancy, renderGuestNotesHtml, initGuestNotes
} from './table-guest-notes.js';

// S201 REF-7e — extrahierte Board-Module (Null Verhaltensänderung). Kein Sub-Modul
// importiert diesen Orchestrator zurück (acyclisch).
import {
  COLUMNS, STATUS_LABEL, STATUS_BADGE_CLASS,
  todayISO, minutesUntilPickup, orderSortByPickup,
  getActiveOrders, getFinishedToday, getCarryOverOrders, ensureActiveItemsLoaded,
  getStaleUnpaidDineInOrders, orderGrossCents, sumOrderGrossCents
} from './orders-data.js';
import {
  orderSetStatus, orderCancel, orderMarkPaid, orderMarkSeen,
  rehydrateOrderSeenFlags, orderProbesaft, shouldPromptReceiptOnPaid
} from './orders-actions.js';
import {
  invalidateSchwundToday, ensureSchwundTodayLoaded, _aggregateSchwundToday,
  renderSchwundTodaySection, getSchwundToday
} from './orders-schwund.js';
import {
  renderOrderCard, renderOrderCardCashout, renderOrderCardCompact,
  renderCarryOverCard, renderOrderItemsBlock, renderStatusActions
} from './orders-cards.js';

// S67 Refactor — Correction + Receipt-Action Sub-Module: intern genutzt (Detail-
// Modal, Auto-Archiv) + unten re-exportiert für externe Caller/Tests.
import {
  openCorrectionDialog,
  closeCorrectionDialog,
  submitCorrectionDialog,
  openPartialCorrectionDialog,
  closePartialCorrectionDialog,
  setPartialCorrectionQty,
  submitPartialCorrectionDialog,
  submitCorrectionItemDialog,
  buildPartialCorrectionReceiptHtml,
  openCorrectionReceiptModal,
  printCorrectionReceiptToPrinter,
  _archivePartialCorrectionBon
} from './orders-correction.js';
import {
  openReceiptActionModal,
  closeReceiptActionModal,
  receiptActionSend,
  receiptActionPrint,
  receiptActionSave,
  resendArchivedBonEmail,
  _ensureBonArchived,
  loadBonArchive,
  buildStandaloneReceiptHtml
} from './orders-receipt-actions.js';

function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { orders: [], settings: {} };
}

// -----------------------------------------------------------------------------
// Live-View-Registry (Phase 23): Tab-versteckt → dirty markieren → beim
// Sichtbarwerden nachholen. Memory feedback_gg_no_view_hijack.
// -----------------------------------------------------------------------------
let _ordersLiveRegistered = false;
function ensureOrdersLiveRegistered() {
  if (_ordersLiveRegistered) return;
  _ordersLiveRegistered = true;
  registerLiveView({
    tabId: 'tab-orders',
    render: renderOrders,
    events: [
      'order:realtime',
      'order:status_changed',
      'order:created',
      'order:cancelled',
      'order:paid'
    ]
  });
}

// -----------------------------------------------------------------------------
// Kanban-Render (Orchestrator)
// -----------------------------------------------------------------------------
/** S233 Ticket B — Tisch-Label einer (Dine-In-)Order auflösen (Snapshot-Feld →
 *  tables-Lookup → '—'). Rein defensiv, kein DB-Touch. */
function _orderTableLabel(o) {
  const direct = o.tableName || o.table_name;
  if (direct) return String(direct);
  const tid = o.tableId || o.table_id;
  if (!tid) return '';
  const tables = (getState().tables || []);
  const t = tables.find(x => x && x.id === tid);
  return (t && t.name) ? String(t.name) : '';
}

/**
 * S233 Ticket B — Warnblock für offene, unbezahlte Dine-In-Orders aus VORTAGEN.
 * Genau der S232-Fall (dine-in, ready/unpaid, kein pickup_date → in keinem
 * datumsbasierten Board-Filter sichtbar). Non-destruktiv, öffnet das bestehende
 * Detail-Modal (openOrderDetail); baut KEINE neuen Aktionen. Leerer String bei N=0.
 */
function renderStaleDineInWarning(list) {
  if (!list || !list.length) return '';
  const sum = sumOrderGrossCents(list);
  const rows = list.map(o => {
    const num  = htmlEscape(String(o.orderNumber || o.order_number || '—'));
    const created = o.createdAt || o.created_at;
    const day  = created ? htmlEscape(fmtDateShort(dateToISO(new Date(created)))) : '—';
    const name = htmlEscape(String(o.customerName || o.customer_name || 'Gast'));
    const tbl  = _orderTableLabel(o);
    const tblStr = tbl ? ` · Tisch ${htmlEscape(tbl)}` : '';
    const cents = orderGrossCents(o);
    const id   = htmlEscape(String(o.id || ''));
    return `
      <li class="dc-open-order">
        <span class="dc-open-order-info">
          <strong>#${num}</strong> · ${day} · ${name}${tblStr} ·
          <strong>${htmlEscape(fmtEUR(cents / 100))}</strong>
        </span>
        <button type="button" class="btn btn-ghost btn-xs" onclick="openOrderDetail('${id}')">Detail</button>
      </li>`;
  }).join('');
  const n = list.length;
  return `
    <details class="orders-carryover orders-stale-dinein" open>
      <summary>⚠️ Unbezahlte Dine-In-Bestellungen aus Vortagen (${n}) · ${htmlEscape(fmtEUR(sum / 100))}</summary>
      <div class="orders-carryover-body orders-stale-dinein-body">
        <p class="hint" style="margin:6px 0 8px;">
          Diese Tisch-Bestellungen sind aus Vortagen noch offen (unbezahlt) — sie halten
          ggf. eine Belegung blockiert. Bitte kassieren oder stornieren (Detail öffnen).
        </p>
        <ul class="dc-open-order-list">${rows}</ul>
      </div>
    </details>
  `;
}

/* S245 Cut C — je Container nur EIN Zeichner; `renderOrders` laeuft bei jedem
   Realtime-Tick, ohne diesen Guard sammelten sich die Listener. */
let _ordersBandUnsub = null;
function _tableNameForBand(tableId) {
  const st = (window.App && window.App.storage && window.App.storage._getInternalState()) || {};
  const t = (st.tables || []).find(x => x.id === tableId);
  return t ? t.name : null;
}
function _mountOrdersSignalBand() {
  if (_ordersBandUnsub) { renderSignalBandInto('orders-signal-band', _tableNameForBand); return; }
  _ordersBandUnsub = mountSignalBand('orders-signal-band', _tableNameForBand);
}

/* S253 — dieselbe Ein-Zeichner-Regel für die Küchen-Hinweise: EIN Abo, und ein
   Neuzeichnen des Boards, sobald ein Gast seinen Hinweis ändert. Ohne das
   stünde eine nachträglich getippte Allergie erst auf der Karte, wenn zufällig
   etwas anderes ein Render auslöst. */
let _ordersNotesUnsub = null;
function _mountOrdersGuestNotes() {
  if (_ordersNotesUnsub) return;
  _ordersNotesUnsub = initGuestNotes(() => {
    const wrap = document.getElementById('tab-orders');
    if (wrap && wrap.offsetParent !== null) renderOrders();
  });
}

export function renderOrders() {
  ensureOrdersLiveRegistered();
  const wrap = document.getElementById('tab-orders');
  if (!wrap) return;
  _mountOrdersGuestNotes();

  // Seen-Markers aus localStorage zurückspielen (billig, bei jedem Render OK).
  rehydrateOrderSeenFlags();

  const active = getActiveOrders();
  const finished = getFinishedToday();
  const carryOver = getCarryOverOrders();
  // S233 Ticket B — offene unbezahlte Dine-In-Orders aus Vortagen (S232-Leak).
  const staleDineIn = getStaleUnpaidDineInOrders();

  // S104-A: Items lazy nachladen (Batch + genau ein nicht-destruktives Re-Render).
  ensureActiveItemsLoaded(active.concat(finished, carryOver));

  // S248 — Teilzahlungen der sichtbaren Bestellungen nachladen: EINE Abfrage,
  // und nur bei echter Änderung ein Re-Render (sonst flackerte das Board bei
  // jedem Realtime-Tick). Seit phase_182 stupst jede Beleg-Änderung die Order
  // an, also kommt eine Online-Zahlung ohne Reload hier an.
  // S249 (B1) — die erledigten Karten gehoeren dazu: eine komplett gesplittete
  // Bestellung ist `paid`, und ohne ihre Belege stuende dort weder „X € bezahlt"
  // noch der richtige Kopfbetrag. Es bleibt EINE Abfrage.
  const _payIds = active.concat(carryOver, staleDineIn, finished).map(o => o.id);
  refreshReceiptSummaries(_payIds).then(changed => {
    if (changed && document.getElementById('tab-orders')) renderOrders();
  }).catch(() => {});

  // S136 Cut A — „Schwund heute" lazy nachladen (fire-and-forget, ein Re-Render
  // über den Callback = renderOrders; kein Orchestrator-Import im Schwund-Modul).
  ensureSchwundTodayLoaded(renderOrders);

  const counts = { incoming: 0, preparing: 0, ready: 0 };
  active.forEach(o => {
    for (const col of COLUMNS) {
      if (col.statuses.includes(o.status)) { counts[col.id]++; break; }
    }
  });

  // Phase 22 — „N offen"-Counter aus geteilter Status-Klassifizierung (nur Orders,
  // die wirklich auf eine Bediener-Aktion warten: pending = annehmen, ready = ausgeben).
  const bucketCounts = countOrdersByBucket(active);
  const operatorPending = bucketCounts.awaitingOperator;
  const activeTotal     = bucketCounts.active;

  const columnsHtml = COLUMNS.map(col => {
    // S60 FIX.5 — `requireUnpaid` filtert in „Kassieren" bereits bezahlte picked_up raus.
    const list = active.filter(o => {
      if (!col.statuses.includes(o.status)) return false;
      if (col.requireUnpaid) {
        const paid = (o.paymentStatus || o.payment_status) === 'paid';
        if (paid) return false;
      }
      return true;
    });
    return `
      <div class="orders-col" data-col="${col.id}">
        <div class="orders-col-header">
          <span>${col.label}</span>
          <span class="orders-col-count">${list.length}</span>
        </div>
        <div class="orders-col-body">
          ${list.length === 0
            ? `<div class="orders-empty">Keine Bestellungen</div>`
            : list.map(col.id === 'cashout' ? renderOrderCardCashout : renderOrderCard).join('')}
        </div>
      </div>
    `;
  }).join('');

  const finishedHtml = `
    <details class="orders-finished" ${finished.length === 0 ? '' : ''}>
      <summary>Erledigt heute (${finished.length})</summary>
      <div class="orders-finished-body">
        ${finished.length === 0
          ? `<div class="orders-empty">Heute noch nichts abgeschlossen.</div>`
          : finished.map(renderOrderCardCompact).join('')}
      </div>
    </details>
  `;

  // S100-A — Carry-Over-Sektion direkt über „Erledigt heute". Default aufgeklappt
  // (operatives Warnsignal), ganz weg bei N=0.
  const carryOverHtml = carryOver.length > 0 ? `
    <details class="orders-carryover" open>
      <summary>⚠️ Überfällig — von Vortagen (${carryOver.length})</summary>
      <div class="orders-carryover-body">
        ${carryOver.map(renderCarryOverCard).join('')}
      </div>
    </details>
  ` : '';

  wrap.innerHTML = `
    <!-- S245 Cut C — dasselbe Hinweisband wie im Belegungsplan, gespeist aus
         derselben Komponente (ein Loader, ein Realtime-Abo). Wer am Board steht,
         soll nicht in den Belegungs-Tab wechseln muessen, um zu sehen, dass
         Tisch 4 seit vier Minuten winkt. -->
    <div id="orders-signal-band"></div>
    <div class="orders-toolbar">
      <div class="orders-toolbar-info">
        <h2>Bestellungen</h2>
        <span class="orders-counter ${operatorPending > 0 ? 'pulse' : ''}" id="orders-open-counter"
              title="${operatorPending} wartet auf Bediener-Aktion (annehmen oder ausgeben) · ${activeTotal} insgesamt aktiv">
          ${operatorPending > 0
            ? operatorPending + ' wartet auf dich'
            : (activeTotal > 0 ? activeTotal + ' aktiv' : 'Alles erledigt')}
        </span>
        ${carryOver.length > 0
          ? `<span class="orders-carryover-indicator" title="${carryOver.length} offene Bestellung(en) von Vortagen — bitte abschließen oder stornieren">⚠️ ${carryOver.length} überfällig</span>`
          : ''}
      </div>
      <div class="orders-toolbar-actions">
        <button id="orders-notify-toggle"
                class="btn btn-ghost btn-sm"
                onclick="toggleOrderPushNotifications()"
                title="Browser-Benachrichtigungen bei neuer Bestellung">
          🔔 Benachrichtigungen
        </button>
        <button class="btn btn-ghost btn-sm" onclick="rerenderActiveTab()" title="Neu laden">
          ↻
        </button>
      </div>
    </div>

    <div class="orders-kanban">
      ${columnsHtml}
    </div>

    ${renderStaleDineInWarning(staleDineIn)}
    ${carryOverHtml}
    ${finishedHtml}
    ${renderSchwundTodaySection(getSchwundToday())}
  `;

  // Update Push-Button-State (in Phase 6 implementiert)
  if (typeof window.refreshOrdersPushButton === 'function') {
    try { window.refreshOrdersPushButton(); } catch (_) {}
  }
  // Band NACH dem innerHTML mounten — der Container entsteht gerade eben.
  _mountOrdersSignalBand();
}

// -----------------------------------------------------------------------------
// Order-Detail-Modal mit Status-Historie
// -----------------------------------------------------------------------------

/**
 * S143c FIX 1 — Liefergebühr als eigene Positions-Zeile im Order-Detail-Modal,
 * damit die Positionen aufs Brutto aufgehen. Selbe Quelle wie der Kassenbon
 * (`deliveryFeeReceiptInfo`), nur delivery mit fee > 0. Pure — testbar ohne DOM.
 */
export function _deliveryFeeDetailRow(order, withActionCol) {
  const feeInfo = deliveryFeeReceiptInfo(order);
  if (!feeInfo) return '';
  return `
            <tr>
              <td><div class="order-modal-item-name">Liefergebühr</div></td>
              <td class="ta-r"></td>
              <td class="ta-r"></td>
              <td class="ta-r">${feeInfo.taxRate}%</td>
              <td class="ta-r"><strong>${fmtEUR(feeInfo.feeCents / 100)}</strong></td>
              ${withActionCol ? '<td class="ta-r"></td>' : ''}
            </tr>`;
}

// S152 A2 — komponierte Nährwert-Zeile je Bestellposition (Flag ingredientsDb).
// Synchron aus dem Nährwert-Cache (composeNutrition-Spiegel), kein RPC-Roundtrip.
function _composedNutritionLine(it) {
  if (!(window.AppConfig && window.AppConfig.features && window.AppConfig.features.ingredientsDb)) return '';
  const cache = window.App && window.App.nutritionCache;
  if (!cache || !it) return '';
  const mid = it.menuItemId || it.menu_item_id;
  if (!mid) return '';
  let n = null;
  try { n = cache.composeLineSync(mid, it.customizations || {}); } catch (_) { return ''; }
  if (!n) return '';
  return `<div class="order-modal-item-nutri" style="font-size:.78rem; color:var(--ink-soft); margin-top:3px;">
      <strong>${n.energyKcal} kcal</strong> · F ${n.fatG} · KH ${n.carbsG} · E ${n.proteinG} · Salz ${n.saltG} g${n.missingData ? ' · ⚠️ unvollständig' : ''}
    </div>`;
}

export async function openOrderDetail(id) {
  const state = getState();
  const cached = (state.orders || []).find(x => x.id === id);
  if (!cached) return showToast('Bestellung nicht gefunden', 'error');

  // Header frisch aus DB lesen (TSE-Felder aktuell; Realtime kann nachhinken —
  // Memory feedback_gg_export_fresh_db). Fallback auf Cache bei DB-Read-Fehler.
  let o = cached;
  try {
    const fresh = await window.App.storage.orders.getFresh(id);
    if (fresh) o = fresh;
  } catch (_) {}

  // Items aus DB nachladen (Cache hat nur den Header).
  let items = [];
  try { items = await window.App.storage.orders.listItems(id); } catch (_) {}

  // S152 A2 — Nährwert-Cache vorladen (Flag ingredientsDb), damit die komponierten
  // Nährwerte je Position synchron im Item-Render verfügbar sind.
  if (window.AppConfig && window.AppConfig.features && window.AppConfig.features.ingredientsDb
      && window.App && window.App.nutritionCache) {
    try { await window.App.nutritionCache.ensureLoaded(); } catch (_) {}
  }

  // Kombinierter Snapshot für den Bon-Renderer (Single-Source mit Tagesabschluss).
  const orderForReceipt = (items && items.length) ? { ...o, preorderSnapshot: {
      version: 2,
      items: items.map(it => ({
        name:             it.nameSnapshot || it.name_snapshot,
        qty:              it.qty,
        unit_price_cents: it.unitPriceCents || it.unit_price_cents,
        line_total_cents: it.lineTotalCents || it.line_total_cents,
        tax_rate:         it.taxRate || it.tax_rate,
        note:             it.notes || (it.customizations && it.customizations.note)
      }))
    } } : o;
  // Globale Referenz für den Print-Button im Modal.
  _detailOrderForReceipt = orderForReceipt;

  const num    = o.orderNumber || o.order_number || '—';
  const name   = o.customerName || o.customer_name || '';
  const email  = o.customerEmail || o.customer_email || '';
  const phone  = o.customerPhone || o.customer_phone || '';
  const date   = o.pickupDate || o.pickup_date;
  const time   = shortTime(o.pickupTime || o.pickup_time);
  const statusLabel = STATUS_LABEL[o.status] || o.status;
  const statusClass = STATUS_BADGE_CLASS[o.status] || '';

  // S63 Block S3 — Item-Level-Storno-Spalte sichtbar bei paid + owner/manager.
  const canCorrect = ((o.paymentStatus || o.payment_status) === 'paid')
    && !o.isCorrection
    && (typeof window !== 'undefined' && window.App && window.App.auth
        && typeof window.App.auth.canEdit === 'function'
        && window.App.auth.canEdit('order.correction'));

  const itemsHtml = items.length ? `
    <table class="order-modal-items">
      <thead>
        <tr>
          <th>Position</th>
          <th class="ta-r">Menge</th>
          <th class="ta-r">Einzel</th>
          <th class="ta-r">MwSt</th>
          <th class="ta-r">Zeile</th>
          ${canCorrect ? '<th class="ta-r">Aktion</th>' : ''}
        </tr>
      </thead>
      <tbody>
        ${items.map(it => {
          const cust = it.customizations || {};
          const note = it.notes || cust.note || '';
          const customizationLines = formatCustomizations(cust);
          return `
            <tr>
              <td>
                <div class="order-modal-item-name">${htmlEscape(it.nameSnapshot || it.name_snapshot || '')}</div>
                ${note ? `<div class="order-modal-item-note">${htmlEscape(note)}</div>` : ''}
                ${customizationLines.length
                  ? `<ul class="order-modal-item-customizations">
                       ${customizationLines.map(l => `<li>${htmlEscape(l)}</li>`).join('')}
                     </ul>`
                  : ''}
                ${_composedNutritionLine(it)}
              </td>
              <td class="ta-r">${it.qty}×</td>
              <td class="ta-r">${fmtEUR(((it.unitPriceCents || it.unit_price_cents) || 0) / 100)}</td>
              <td class="ta-r">${it.taxRate || it.tax_rate}%</td>
              <td class="ta-r"><strong>${fmtEUR(((it.lineTotalCents || it.line_total_cents) || 0) / 100)}</strong></td>
              ${canCorrect ? `<td class="ta-r"><button class="btn btn-ghost btn-sm" style="color:var(--danger);padding:2px 8px;" onclick="submitCorrectionItemDialog('${htmlEscape(o.id)}','${htmlEscape(it.id)}')" title="Diese Position stornieren">✕ Storno</button></td>` : ''}
            </tr>
          `;
        }).join('')}
        ${_deliveryFeeDetailRow(o, canCorrect)}
      </tbody>
      <tfoot>
        <tr>
          <td colspan="${canCorrect ? 5 : 4}" class="ta-r">Brutto</td>
          <td class="ta-r"><strong>${fmtEUR(((o.totalGrossCents || o.total_gross_cents) || 0) / 100)}</strong></td>
        </tr>
      </tfoot>
    </table>
  ` : (renderOrderItemsBlock(o) || '<p class="meta" style="padding:16px;">Keine Positionen geladen.</p>');

  // Status-Historie als moderne Timeline
  const history = buildOrderStatusHistory(o);
  const timelineHtml = history.map((h, idx) => `
    <li class="order-timeline-item ${idx === history.length - 1 ? 'order-timeline-item-current' : ''}">
      <span class="order-timeline-dot" aria-hidden="true"></span>
      <div class="order-timeline-content">
        <div class="order-timeline-label">${htmlEscape(h.label)}</div>
        <div class="order-timeline-ts">${htmlEscape(h.ts)}</div>
      </div>
    </li>
  `).join('');

  // Bezahl-Info
  const paid = (o.paymentStatus || o.payment_status) === 'paid';

  // S248 — die Belege dieser Bestellung frisch holen: „Noch nicht bezahlt" war
  // seit Cut D irreführend, sobald ein Gast seinen Teil online beglichen hatte.
  // Der Gesamtbetrag steht in der Positions-Tabelle darüber; hier geht es um
  // die Frage, die der Service stellt — was ist noch zu kassieren?
  try { await refreshReceiptSummaries([id]); } catch (_) { /* nicht-fatal */ }
  const partPay = partialPayFor(o);

  const paymentBlock = paid
    ? `<div class="order-modal-pay order-modal-pay-paid">
         <strong>✓ Bezahlt</strong>
         <span>${htmlEscape(o.paymentMethod || o.payment_method || '—')}</span>
         ${o.invoiceNumber || o.invoice_number ? `<span>Rechnungs-Nr ${htmlEscape(String(o.invoiceNumber || o.invoice_number))}</span>` : ''}
       </div>`
    : partPay
      ? `<div class="order-modal-pay order-modal-pay-open">
           <strong>Teilweise bezahlt</strong>
           <span>${htmlEscape(partPay.text)}</span>
           <span>Gesamt ${htmlEscape(fmtEUR(((o.totalGrossCents || o.total_gross_cents) || 0) / 100))}</span>
         </div>`
      : `<div class="order-modal-pay order-modal-pay-open">
           <strong>Noch nicht bezahlt</strong>
           <span>Bezahlung kann über die Kanban-Karte gebucht werden.</span>
         </div>`;

  // S253 — alle aktuellen Hinweise dieser Belegung. „Aktuell" ist wörtlich
  // gemeint: gelesen wird beim Öffnen des Modals, nicht beim Absenden der
  // Runde eingefroren (Jonas-Entscheid „gilt rückwirkend").
  const guestNoteHtml = o.occupancyId
    ? renderGuestNotesHtml(notesForOccupancy(guestNotes(), o.occupancyId), { cls: 'oc' })
    : '';

  const modal = document.createElement('div');
  modal.className = 'order-modal-overlay';
  modal.id = 'order-detail-modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-labelledby', 'order-modal-title');
  modal.innerHTML = `
    <div class="order-modal" onclick="event.stopPropagation()">
      <header class="order-modal-header">
        <div>
          <div class="order-modal-eyebrow">Bestellung</div>
          <h2 id="order-modal-title" class="order-modal-title">
            ${htmlEscape(String(num))}
            <span class="order-modal-status badge ${statusClass}">${htmlEscape(statusLabel)}</span>
          </h2>
        </div>
        <button class="order-modal-close" onclick="closeOrderDetail()" aria-label="Schließen">×</button>
      </header>

      ${guestNoteHtml}

      <div class="order-modal-actions">
        <button class="btn btn-ghost btn-sm" onclick="previewOrderReceipt()"
                title="Den ausgestellten Bon mit Logo und allen Pflichtangaben anzeigen">
          👁 Bon ansehen
        </button>
        <button class="btn btn-primary btn-sm" onclick="printOrderReceiptFromModal()">
          📋 ${o.isCorrection ? 'Stornobeleg-Aktion' : 'Beleg-Aktion'}
        </button>
        <button class="btn btn-ghost btn-sm" onclick="openProcessHistory('${htmlEscape(o.id)}')"
                title="Druckbares Vorgangsprotokoll: alle Phasen mit Zeiten und Operatoren (aus dem revisionssicheren Audit-Log)">
          🧾 Prozessübersicht
        </button>
        ${(() => {
          // Phase 31 / Block C (S29): „Beleg aus Archiv versenden" — nur sichtbar,
          // wenn der Bon im localStorage-Archiv liegt.
          try {
            const arch = loadBonArchive();
            const entry = arch && arch[o.id];
            // S240-HF7: `entry.html` ist im Listen-Cache lazy (null) — die
            // EXISTENZ des Archiv-Eintrags entscheidet über den Button, den
            // Snapshot holt der Versand-Pfad selbst nach.
            if (!entry) return '';
            return `
              <button class="btn btn-ghost btn-sm"
                      onclick="resendArchivedBonEmail('${htmlEscape(o.id)}')"
                      title="Sendet den archivierten Bon-Snapshot per Mail an Kunde/Steuerberater">
                ✉ Beleg aus Archiv versenden
              </button>`;
          } catch (_) { return ''; }
        })()}
        ${o.isCorrection ? `
          <span class="order-modal-correction-info" style="margin-left:auto;color:var(--danger);font-size:0.85rem;">
            Stornobeleg zu ${htmlEscape(String(o.correctsOrderId || '—'))}
          </span>`
        : o.correctedByOrderId ? `
          <span class="order-modal-correction-info" style="margin-left:auto;color:var(--danger);font-size:0.85rem;">
            Storniert via Korrekturbeleg
            <button class="btn btn-ghost btn-xs" style="margin-left:6px;"
                    onclick="openOrderDetail('${htmlEscape(String(o.correctedByOrderId))}')">öffnen</button>
          </span>`
        : o.status === 'cancelled' || o.status === 'no_show' ? ''
        : o.paymentStatus === 'paid' ? `
          <div style="margin-left:auto;display:flex;gap:6px;">
            <button class="btn btn-ghost btn-sm" style="color: var(--danger, #b94a48);"
                    onclick="openPartialCorrectionDialog('${o.id}')"
                    title="Einzelne Positionen stornieren (S60 Teilstorno)">
              ↩ Teil-Storno
            </button>
            <button class="btn btn-ghost btn-sm" style="color: var(--danger, #b94a48);"
                    onclick="openCorrectionDialog('${o.id}')"
                    title="Komplette Bestellung stornieren">
              ↩ Storno (gesamt)
            </button>
          </div>`
        : `
          <button class="btn btn-ghost btn-sm" style="color: var(--danger, #b94a48); margin-left: auto;"
                  onclick="orderCancel('${o.id}'); closeOrderDetail();">
            Bestellung stornieren
          </button>`}
      </div>

      ${((o.paymentMethod || o.payment_method) === 'stripe') ? (() => {
        // S234 T4 — Online-Zahlung sichtbar + PaymentIntent (Refund = manuell).
        const pi = o.stripePaymentIntentId || o.stripe_payment_intent_id || '';
        const paidLbl = (o.paymentStatus === 'paid') ? 'Online bezahlt' : 'Online-Zahlung ausstehend';
        return `
      <div class="order-modal-stripe-info" style="margin:8px 16px 0;padding:10px 12px;background:#f0f7ff;border:1px solid #b8d4f5;border-radius:8px;color:#16221c;font-size:.88rem;">
        <strong>💳 ${paidLbl} (Stripe)</strong>
        ${pi ? ` · PaymentIntent: <code style="word-break:break-all;">${htmlEscape(pi)}</code>
          <button class="btn btn-ghost btn-xs" style="margin-left:6px;" onclick="copyStripePaymentIntent('${htmlEscape(pi)}')" title="PaymentIntent-Id kopieren (Refund im Stripe-Dashboard)">📋 Kopieren</button>` : ''}
        <div style="margin-top:4px;color:#4a5a6e;">Rückerstattungen erfolgen aktuell <strong>manuell</strong> im Stripe-Dashboard.</div>
      </div>`;
      })() : ''}

      <div class="order-modal-body">
        <section class="order-modal-grid">
          <div class="order-modal-card">
            <h3>Kunde</h3>
            <div class="order-modal-customer-name">${htmlEscape(name) || '—'}</div>
            ${phone ? `<a class="order-modal-link" href="tel:${htmlEscape(phone)}">📞 ${htmlEscape(phone)}</a>` : ''}
            ${email ? `<a class="order-modal-link" href="mailto:${htmlEscape(email)}">✉ ${htmlEscape(email)}</a>` : ''}
            ${o.publicStatusToken ? `<button class="btn btn-ghost btn-xs" style="margin-top:6px;"
                    onclick="copyOrderTrackingLink('${htmlEscape(o.id)}')"
                    title="Live-Status-Link für den Kunden kopieren (z.B. telefonisch durchgeben)">
              🔗 Tracking-Link kopieren
            </button>` : ''}
          </div>
          <div class="order-modal-card">
            <h3>Abholung</h3>
            <div class="order-modal-pickup-date">${date ? fmtDate(date) : '—'}</div>
            <div class="order-modal-pickup-time">${time || '—'} Uhr</div>
          </div>
          <div class="order-modal-card order-modal-card-wide">
            ${paymentBlock}
          </div>
        </section>

        <section class="order-modal-section">
          <h3>Positionen</h3>
          ${itemsHtml}
        </section>

        <section class="order-modal-section">
          <h3>Status-Verlauf</h3>
          <ol class="order-timeline">
            ${timelineHtml || '<li class="order-timeline-empty">Keine Status-Wechsel hinterlegt.</li>'}
          </ol>
        </section>
      </div>
    </div>
  `;
  // Backdrop-Klick schließt
  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeOrderDetail();
  });
  // ESC schließt
  const onKey = (e) => {
    if (e.key === 'Escape') { closeOrderDetail(); document.removeEventListener('keydown', onKey); }
  };
  document.addEventListener('keydown', onKey);
  document.body.appendChild(modal);
}

// Modul-private Referenz auf die aktive Order für den Bon-Druck-Button.
let _detailOrderForReceipt = null;

/**
 * Beleg-Aktion-Modal (statt direkter Browser-Druckvorschau). Öffnet dasselbe
 * Modal wie nach «Bezahlt» — Bediener entscheidet: Versenden / Drucken /
 * Archivieren / Bon anzeigen.
 */
export function printOrderReceiptFromModal() {
  if (!_detailOrderForReceipt) {
    showToast('Keine Bestelldaten zum Drucken.', 'error');
    return;
  }
  const kind = _detailOrderForReceipt.isCorrection ? 'correction' : 'paid';
  openReceiptActionModal(_detailOrderForReceipt, { kind });
}

/**
 * „Bon ansehen"-Vorschau: fertig gerenderter Bon (renderOrderReceipt = identischer
 * Output wie der Druck) in einem eigenen Modal, mit Direkt-Druck + Beleg-Aktion.
 */
export function previewOrderReceipt() {
  if (!_detailOrderForReceipt) {
    showToast('Keine Bestelldaten zur Vorschau.', 'error');
    return;
  }
  const order = _detailOrderForReceipt;
  const html = renderOrderReceipt(order);
  const num = htmlEscape(String(order.orderNumber || order.id));

  const existing = document.getElementById('order-receipt-preview-modal');
  if (existing) existing.remove();

  const modal = document.createElement('div');
  modal.className = 'order-modal-overlay';
  modal.id = 'order-receipt-preview-modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.innerHTML = `
    <div class="order-modal" onclick="event.stopPropagation()" style="max-width:640px;">
      <header class="order-modal-header">
        <div>
          <div class="order-modal-eyebrow">Bon-Vorschau</div>
          <h2 class="order-modal-title">Bestellung ${num}</h2>
        </div>
        <button class="order-modal-close" onclick="closeOrderReceiptPreview()" aria-label="Schließen">×</button>
      </header>
      <div class="order-modal-body" style="max-height: 70vh; overflow-y: auto; padding: 16px 20px; background: var(--bg-soft);">
        ${html}
      </div>
      <div class="order-modal-actions" style="border-top:1px solid var(--border, #e3ddd0); padding: 12px 20px;">
        <button class="btn btn-primary btn-sm" onclick="closeOrderReceiptPreview(); printOrderReceiptDirect();">
          🖨️ Direkt drucken
        </button>
        <button class="btn btn-ghost btn-sm" onclick="closeOrderReceiptPreview(); printOrderReceiptFromModal();">
          📋 Beleg-Aktion (Versenden / Archivieren)
        </button>
        <button class="btn btn-ghost btn-sm" style="margin-left:auto;"
                onclick="closeOrderReceiptPreview()">Schließen</button>
      </div>
    </div>
  `;
  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeOrderReceiptPreview();
  });
  const onKey = (e) => {
    if (e.key === 'Escape') {
      closeOrderReceiptPreview();
      document.removeEventListener('keydown', onKey);
    }
  };
  document.addEventListener('keydown', onKey);
  document.body.appendChild(modal);

  // S54 — QR-SVG nachladen (async, ersetzt den Placeholder im TSE-Block)
  attachReceiptTseQr(modal).catch((err) => {
    console.warn('[previewOrderReceipt] QR-Attach fehlgeschlagen:', err);
  });
}

export function closeOrderReceiptPreview() {
  const m = document.getElementById('order-receipt-preview-modal');
  if (m) m.remove();
}

/** Bondrucker-Druck direkt aus der Vorschau heraus (ohne Aktion-Modal-Umweg). */
export function printOrderReceiptDirect() {
  if (!_detailOrderForReceipt) return;
  try { printOrderReceipt(_detailOrderForReceipt); }
  catch (e) { console.error('[printOrderReceiptDirect]', e); showToast('Druck fehlgeschlagen: ' + (e.message || e), 'error'); }
}

export function closeOrderDetail() {
  const m = document.getElementById('order-detail-modal');
  if (m) m.remove();
  _detailOrderForReceipt = null;
}

/** REF-7c: Tracking-Link der Order in die Zwischenablage kopieren (Fallback:
 *  styledPrompt, wenn die Clipboard-API fehlt). */
export async function copyOrderTrackingLink(id) {
  const order = (window.App.storage._getInternalState().orders || []).find(o => o.id === id);
  if (!order) { showToast('Bestellung nicht gefunden.', 'error'); return; }
  const link = buildOrderTrackingLink(order);
  if (!link) { showToast('Kein Tracking-Token für diese Bestellung vorhanden.', 'error'); return; }
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(link);
      showToast('Tracking-Link kopiert.', 'success');
    } else {
      await window.styledPrompt({ title: 'Tracking-Link', label: 'Manuell kopieren (Strg+C):', value: link, confirmLabel: 'Schließen', required: false });
    }
  } catch (e) {
    console.error('[copyOrderTrackingLink]', e);
    await window.styledPrompt({ title: 'Tracking-Link', label: 'Manuell kopieren (Strg+C):', value: link, confirmLabel: 'Schließen', required: false });
  }
}

/** S234 T4 — Stripe-PaymentIntent-Id in die Zwischenablage (für den manuellen
 *  Refund im Stripe-Dashboard). Fallback: styledPrompt zum manuellen Kopieren. */
export async function copyStripePaymentIntent(pi) {
  const val = String(pi || '').trim();
  if (!val) { showToast('Keine PaymentIntent-Id vorhanden.', 'error'); return; }
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(val);
      showToast('PaymentIntent-Id kopiert.', 'success');
    } else {
      await window.styledPrompt({ title: 'PaymentIntent-Id', label: 'Manuell kopieren (Strg+C):', value: val, confirmLabel: 'Schließen', required: false });
    }
  } catch (e) {
    console.error('[copyStripePaymentIntent]', e);
    await window.styledPrompt({ title: 'PaymentIntent-Id', label: 'Manuell kopieren (Strg+C):', value: val, confirmLabel: 'Schließen', required: false });
  }
}

function buildOrderStatusHistory(o) {
  const map = [
    ['createdAt',    'created_at',    'Eingegangen'],
    ['confirmedAt',  'confirmed_at',  'Angenommen'],
    ['preparingAt',  'preparing_at',  'In Zubereitung'],
    ['readyAt',      'ready_at',      'Bereit'],
    ['pickedUpAt',   'picked_up_at',  'Abgeholt'],
    ['cancelledAt',  'cancelled_at',  'Storniert'],
    ['noShowAt',     'no_show_at',    'No-Show'],
    ['paidAt',       'paid_at',       'Bezahlt']
  ];
  const out = [];
  for (const [camel, snake, label] of map) {
    const v = o[camel] || o[snake];
    if (v) out.push({ label, ts: formatTs(v) });
  }
  return out;
}

// -----------------------------------------------------------------------------
// Re-Exports — Board-Handler (extrahierte Module) + Correction/Receipt-Action.
// Erhalten die Backward-Compat: main.js Namespace-Import `adminOrders.*` + Tests
// (`import { x } from './orders.js'`). Die Symbole sind oben bereits importiert.
// -----------------------------------------------------------------------------
export {
  // Board-Handler (orders-actions.js / orders-schwund.js)
  orderSetStatus,
  orderCancel,
  orderMarkPaid,
  orderMarkSeen,
  rehydrateOrderSeenFlags,
  orderProbesaft,
  invalidateSchwundToday,
  _aggregateSchwundToday,
  renderSchwundTodaySection,
  // Voll-Storno
  openCorrectionDialog,
  closeCorrectionDialog,
  submitCorrectionDialog,
  // Teil-Storno
  openPartialCorrectionDialog,
  closePartialCorrectionDialog,
  setPartialCorrectionQty,
  submitPartialCorrectionDialog,
  submitCorrectionItemDialog,
  // Korrektur-Bon-Renderer + Druck + Archivierung
  buildPartialCorrectionReceiptHtml,
  openCorrectionReceiptModal,
  printCorrectionReceiptToPrinter,
  _archivePartialCorrectionBon,
  // Beleg-Aktion-Modal
  openReceiptActionModal,
  closeReceiptActionModal,
  receiptActionSend,
  receiptActionPrint,
  receiptActionSave,
  // Mail-Resend aus Archiv
  resendArchivedBonEmail,
  // Auto-Archivierung (S57 Hard-Way, GoBD §147 AO)
  _ensureBonArchived,
  // DB-Cache-Reader fürs Archiv-Sub-Tab
  loadBonArchive,
  // Standalone-HTML-Renderer (für Mail-Versand + Download)
  buildStandaloneReceiptHtml
};

// -----------------------------------------------------------------------------
// Backward-Compat (inline-onclick-Handler) — nur orchestrator-eigene Funktionen.
// Board-Handler binden sich in orders-actions.js / orders-schwund.js selbst;
// Correction/Receipt-Action in ihren Sub-Modulen (S67/S201).
// -----------------------------------------------------------------------------
if (typeof window !== 'undefined') {
  Object.assign(window, {
    renderOrders,
    openOrderDetail,
    closeOrderDetail,
    copyOrderTrackingLink,
    copyStripePaymentIntent,
    printOrderReceiptFromModal,
    previewOrderReceipt,
    closeOrderReceiptPreview,
    printOrderReceiptDirect
  });
}

// -----------------------------------------------------------------------------
// Test-Hooks (additive named exports für vitest). Pure-Helper werden exposed,
// statt via Reflection auf interne Symbols zuzugreifen (Memory feedback_gg_hard_way).
// -----------------------------------------------------------------------------
export {
  todayISO as _todayISO,
  shortTime as _shortTime,
  minutesUntilPickup as _minutesUntilPickup,
  orderSortByPickup as _orderSortByPickup,
  formatMinutes as _formatMinutes,
  shouldPromptReceiptOnPaid as _shouldPromptReceiptOnPaid,
  getActiveOrders as _getActiveOrders,
  getFinishedToday as _getFinishedToday,
  getCarryOverOrders as _getCarryOverOrders,
  renderStatusActions as _renderStatusActions,
  renderOrderCard as _renderOrderCard
};

// S104-A: `orderChannels` lebt im gemeinsamen Item-Access-Modul; hier
// re-exportiert, damit bestehende Importe (orders.test.js) stabil bleiben.
export { orderChannels };
